import { useDisableLogin, useEnableLogin } from "@/api/mutations/logins";
import type { LoginItemType } from "@/api/queries/logins/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { TableCell } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useState } from "react";
import { toast } from "sonner";

import { LoginRunningState } from "../login-running-state";

interface LoginStatusProps {
  loginItem: LoginItemType;
}

export const LoginStatus = ({ loginItem }: LoginStatusProps) => {
  const { mutateAsync: startLogin, isPending: isStarting } = useEnableLogin(loginItem.id);
  const { mutateAsync: stopLogin, isPending: isStopping } = useDisableLogin(loginItem.id);
  const [openPopover, setOpenPopover] = useState(false);
  const [reason, setReason] = useState("");

  const isPending = isStarting || isStopping;
  const handleToggle = async (login: LoginItemType, reason?: string) => {
    try {
      if (login.active) {
        await stopLogin({ reason });
      } else {
        await startLogin();
      }

      toast.success(`Login ${!login.active ? "enabled" : "disabled"}`);
    } catch {
      toast.error(`Failed to update ${!login.active ? "enable" : "disable"} login.`);
    }
  };

  const noProxyProvided =
    !loginItem.active && loginItem.disabledReason?.toLowerCase() === "no proxy";

  return (
    <TableCell>
      <div className="flex gap-4">
        <div className="flex items-center gap-2">
          {noProxyProvided && (
            <Tooltip>
              <TooltipTrigger>
                <span data-testid="no-proxy-provided" className="text-yellow-500">
                  ⚠️
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>This login has no proxy provided. Please contact administrator.</p>
              </TooltipContent>
            </Tooltip>
          )}
          <Popover open={openPopover} onOpenChange={loginItem.active ? setOpenPopover : undefined}>
            <PopoverTrigger>
              <Switch
                onMouseDown={(e) => e.stopPropagation()}
                checked={loginItem.active}
                onCheckedChange={(checked) => {
                  if (!checked) {
                    setOpenPopover(true);
                  } else {
                    handleToggle(loginItem);
                  }
                }}
                disabled={isPending}
              />
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <div className="space-y-4">
                <h4 className="font-medium leading-none">Confirm Disable</h4>
                <p className="text-sm text-muted-foreground">
                  Are you sure you want to disable this login?
                </p>
                <div className="space-y-2">
                  <Label htmlFor="reason">Reason (optional)</Label>
                  <Input
                    id="reason"
                    placeholder="Enter reason for disabling..."
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setOpenPopover(false);
                      setReason("");
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      handleToggle(loginItem, reason);
                      setOpenPopover(false);
                      setReason("");
                    }}
                    disabled={isPending}
                  >
                    Confirm
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
          <Tooltip>
            <TooltipTrigger disabled={!loginItem.disabledReason || loginItem.active}>
              <span
                className={`text-sm ${!loginItem.active && loginItem.disabledReason ? "cursor-pointer underline decoration-dotted" : ""}`}
              >
                {loginItem.active ? "Active" : "Inactive"}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <div className="space-y-2 p-1">
                <h4 className="font-medium leading-none">Disabled Reason</h4>
                <p className="text-sm text-muted-foreground wrap-break-word whitespace-pre-wrap">
                  {loginItem.disabledReason}
                </p>
              </div>
            </TooltipContent>
          </Tooltip>
        </div>
        {loginItem.active && <LoginRunningState loginItem={loginItem} />}
      </div>
    </TableCell>
  );
};
